// TournamentViewModePage — Admin-only TV/presentation surface for tournaments.
//
// Route: /tournaments/:id/view (wrapped in <AdminRoute> in App.js).
// Renders NO app chrome — no BottomNav, no header, no editing.
//
// View options:
//   • schedule  — MatchScheduleGrid over tournament.matches
//   • bracket   — PresentationBracket (reuses bracket geometry)
//
// Data source: GET /api/tournaments/{id} — same endpoint used by the
// admin management page. No new backend routes.
//
// Refresh: silent poll every 15s. First failure keeps last good data.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { tournamentAPI } from '../lib/tournamentApi';
import ViewModeShell from '../components/viewmode/ViewModeShell';
import MatchScheduleSlates from '../components/viewmode/MatchScheduleSlates';
import PresentationBracket from '../components/viewmode/PresentationBracket';

const REFRESH_MS = 15000;

const VIEW_OPTIONS = [
  { key: 'schedule', label: 'Schedule' },
  { key: 'bracket',  label: 'Bracket'  },
];

export default function TournamentViewModePage() {
  const { id } = useParams();
  const [tournament, setTournament] = useState(null);
  const [err, setErr] = useState(null);
  const [activeView, setActiveView] = useState('schedule');
  const [lastUpdated, setLastUpdated] = useState(null);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const r = await tournamentAPI.get(id);
      if (!mounted.current) return;
      setTournament(r.data);
      setLastUpdated(new Date());
      setErr(null);
    } catch (e) {
      if (!mounted.current) return;
      setErr(e.response?.data?.detail || 'Could not load tournament.');
    }
  }, [id]);

  useEffect(() => {
    mounted.current = true;
    load();
    const int = setInterval(load, REFRESH_MS);
    return () => {
      mounted.current = false;
      clearInterval(int);
    };
  }, [load]);

  if (err && !tournament) {
    // Permission denied or 404 — punt to the tournament detail. This is a
    // secondary defense; the route wrapper already blocks non-admins.
    return <Navigate to={`/tournaments/${id}`} replace />;
  }

  const viewLabel = activeView === 'schedule' ? 'Match Schedule' : 'Bracket';

  return (
    <ViewModeShell
      eventName={tournament?.name}
      viewLabel={viewLabel}
      viewOptions={VIEW_OPTIONS}
      activeView={activeView}
      onViewChange={setActiveView}
      lastUpdated={lastUpdated}
    >
      {!tournament ? (
        <div
          className="h-full flex items-center justify-center text-[#6a766e]"
          style={{ fontSize: 'clamp(14px, 1.2vw, 18px)' }}
          data-testid="tournament-view-loading"
        >
          Loading…
        </div>
      ) : (
        <>
          {/* Both views stay mounted so the schedule keeps its slate across
              Schedule → Bracket → Schedule; `active` suspends its timer. */}
          <div className={activeView === 'schedule' ? 'h-full' : 'hidden'}>
            <MatchScheduleSlates
              matches={tournament.matches || []}
              mode="flat"
              active={activeView === 'schedule'}
              testidPrefix="tournament-view-schedule"
            />
          </div>
          <div className={activeView === 'bracket' ? 'h-full' : 'hidden'}>
            <PresentationBracket tournament={tournament} active={activeView === 'bracket'} />
          </div>
        </>
      )}
    </ViewModeShell>
  );
}
